<template>
  <div
    class="instance-actions__content text-center"
    :class="
      state
        ? state === 'error'
          ? 'instance-actions__content--error'
          : 'instance-actions__content--pending'
        : ''
    "
  >
    <template v-if="!state">
      <v-btn
        color="primary"
        elevation="0"
        small
        max-width="184px"
        min-width="184px"
        :loading="manageSiteLoading"
        @click.stop.prevent="manageSite"
      >
        <span class="font-weight-bold">{{ $t('button.site.manage') }}</span>
      </v-btn>
      <v-btn
        class="mt-4 link-hover"
        color="info"
        elevation="0"
        style="background-color: var(--v-link-base);"
        small
        max-width="184px"
        min-width="184px"
        @click.stop.prevent="$emit('openWpLogin')"
      >
        <v-icon v-if="!checkingWpLogin" class="white-text" size="16">
          $wordpressalt
        </v-icon>
        <loader
          v-else
          class="mr-3 white-text"
          size="16"
          :color="'info'"
          :noMargins="true"
        />
        <span class="font-weight-bold white-text">{{ $t('button.wpAdmin') }}</span>
      </v-btn>

      <v-tooltip
        v-if="previewNotAvailable"
        transition="custom-tooltip"
        open-delay="150"
        bottom
        z-index="99"
        offset-overflow
        nudge-bottom="4px"
      >
        <template v-slot:activator="{ on, attrs }">
          <div
            v-on="on"
            v-bind="attrs"
            class="
              preview-not-available__text
              p-4
              pt-4
              gray--text
              text--darken-2
            "
          >
            {{ $t('message.notPreview.title') }}
            <span class="primary--text">{{ $t('button.why') }}</span>
          </div>
        </template>

        <span v-if="instance.isNew()">
          {{ $t('message.notPreview.info.new') }}
        </span>
        <span v-else>
          {{ $t('message.notPreview.info.other') }}
        </span>
      </v-tooltip>
    </template>
    <template v-else-if="state === 'error'">
      <span v-if="isImport" class="font-weight-bold p2 heading--text">
        {{ $t('message.status.importing.failed') }} {{ $t('button.try') }}
      </span>
      <span v-else class="font-weight-bold p2 heading--text">
        {{ $t('message.status.install.failed') }} {{ $t('button.try') }}
      </span>

      <v-btn
        small
        class="mt-4"
        color="success"
        elevation="0"
        max-width="184px"
        min-width="184px"
        @click.stop.prevent="$emit('restartInstallation')"
      >
        <v-icon color="white" size="16">$restore</v-icon>
        <span class="font-weight-bold">{{ $t('button.try') }}</span>
      </v-btn>
      <v-btn
        small
        class="cancel-installation-btn mt-4"
        color="error"
        outlined
        elevation="0"
        max-width="184px"
        min-width="184px"
        @click.stop.prevent="openCancellationModal()"
      >
        <span class="font-weight-bold">
          <span v-if="isImport">{{ $t('button.import.cancel') }}</span>
          <span v-else>{{ $t('button.installation.cancel') }}</span>
        </span>
      </v-btn>
    </template>
    <template v-else-if="state === 'in_progress'">
      <template v-if="isImport">
        <div class="d-flex align-center font-weight-bold p2 heading--text mb-4">
           <loader
            class="mr-2"
            :noMargins="true"
            :size="14"
            />
          <span>{{ $t('message.status.importing.inProgress') }}</span>
        </div>
      </template>
      <template v-else>
        <div class="d-flex justify-center align-center flex-column font-weight-bold p2 heading--text">
          <p class="d-flex justify-center align-center heading--text mb-1">
            <loader
              class="mr-2"
              :noMargins="true"
              :size="14"

            />
            {{ $t('notification.instance.installation.inProgress') }}
          </p> 
          <p class="heading--text mb-0">{{ $t('message.managementNotAvailable') }}</p>
        </div>
      </template>

      <v-btn
        v-if="isImport"
        small
        color="primary"
        elevation="0"
        max-width="184px"
        min-width="184px"
        @click.stop.prevent="$emit('showImportDetails')"
      >
        <span class="font-weight-bold">{{ $t('button.import.details') }}</span>
      </v-btn>

      <v-btn
        small
        class="cancel-installation-btn mt-4"
        color="error"
        outlined
        elevation="0"
        max-width="184px"
        min-width="184px"
        @click.stop.prevent="openCancellationModal()"
      >
        <span class="font-weight-bold">
          <span v-if="isImport">{{ $t('button.import.cancel') }}</span>
          <span v-else>{{ $t('button.installation.cancel') }}</span>
        </span>
      </v-btn>

    </template>
    <v-dialog
      transition="fade-transition"
      persistent
      ref="dialog"
      elevation="8" 
      v-model="showCancellationModal"
    >
      <v-card class="" width="560px">
        <v-card-title class="d-flex align-center justify-start">
          <v-icon size="20" class="mr-3" color="red">$alertTriangle</v-icon>
          <h6 v-if="isImport">{{ $t('heading.instance.modal.cancelInstanceImport.title') }}</h6>
          <h6 v-else>{{ $t('heading.instance.modal.cancelInstanceInstallation.title') }}</h6>
          <v-btn x-small icon  @click="closeCancellationModal" class="ml-auto">
            <v-icon size="24" color="gray">$close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider class="mb-6" />

        <v-card-text class="">
          <p class="p-d" v-if="isImport">{{ $t('heading.instance.modal.cancelInstanceImport.desc') }}</p>
          <p class="p-d" v-else>{{ $t('heading.instance.modal.cancelInstanceInstallation.desc') }}</p>
          <div class="">
            <h4 class="mb-4 p-2 font-weight-600">
              {{ $t('message.confirmAction') }}
            </h4>
            <label class="base--text checkbox-label d-flex align-center p-2" style="height: 24px;">
              <checkbox
                class=""
                outlined
                :label="isImport ? $t('heading.instance.modal.cancelInstanceImport.checkbox') : $t('heading.instance.modal.cancelInstanceInstallation.checkbox')"
                color="primary"
                dense
                @change="confirmCheckbox"
              ></checkbox>
            </label>
          </div>
        </v-card-text>
        <v-card-actions
          class="pt-7 pb-3 d-flex flex-column align-center justify-space-between"
        >
          <v-btn 
            block
            elevation="0"
            large
            color="red" 
            :disabled="!confirmedCloseModal"
            @click="handleCancel()"
          >
            <span v-if="isImport" class="white--text">{{ $t('button.import.cancel') }}</span>
            <span v-else class="white--text">{{ $t('button.installation.cancel') }}</span>
          </v-btn>
          <v-btn 
            block
            elevation="0"
            large
            text
            class="mt-2"
            @click="closeCancellationModal"
          >
            <span v-if="isImport" class="text--gray-darken1">{{ $t('button.import.resume') }}</span>
            <span v-else class="text--gray-darken1">{{ $t('button.installation.resume') }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Loader from "../Loader.vue";
import checkbox from "@/components/buttons/Checkbox.vue";

export default {
  components: {
    Loader,
    checkbox,
  },
  props: {
    instance: Object,
    checkingWpLogin: Boolean,
    state: {
      type: [String, Boolean],
      default: false,
    },
    previewNotAvailable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      manageSiteLoading: false,
      showCancellationModal: false,
      confirmedCloseModal: false,
    };
  },
  computed: {
    isImport() {
      return this.instance.import_details !== null;
    }
  },
  methods: {
    handleCancel() {
      this.$emit('cancelInstallation')
      this.closeCancellationModal()
    },
    confirmCheckbox() {
      this.confirmedCloseModal = !this.confirmedCloseModal;
    },
    openCancellationModal() {
      console.log('openenesfnjfsnfrsnfsnodsfnjgfdgfd');
      this.showCancellationModal = true;
      this.confirmedCloseModal = false;
    },
    closeCancellationModal() {
      this.showCancellationModal = false;
      this.confirmedCloseModal = false;
    },
    manageSite() {
      this.manageSiteLoading = true;
      this.$nextTick(() => {
        this.$router.push({ path: `/instances/${this.instance.id}` });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.v-card {
  &:hover {
    .instance-actions {
      opacity: 1;
      &__content {
        transform: translateY(0px);
      }
    }
  }
  .instance-actions {
    &__content {
      width: 100%;
      transition: transform 0.3s ease-out;
      transform: translateY(16px);
      position: relative;
      z-index: 9;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    // @media (max-width: 767px) {
    //   .v-btn {
    //     margin-top: 0px !important;
    //     &:not(:first-child) {
    //       margin-left: 16px;
    //     }
    //   }
    // }
  }
}

.link-hover:hover {
  background: var(--v-link-hover) !important;
  border-color: var(--v-link-hover) !important;
}

.cancel-installation-btn{
  &.v-btn.v-btn--outlined{
    .v-btn__content{
      > span{
        color: var(--v-error-base) !important;
      }
    }
    border-color: var(--v-error-base) !important;
  }
}
</style>
